import {
  EventConsts,
  SettingsConsts,
  UserRoleConsts,
  UserScopeConsts,
} from 'client/util/Constants';
import { get, isUndefined } from 'lodash';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import ProvidersApi from 'client/api/v2/ProvidersApi';
import RoleApi from 'api/v2/RoleApi';
import SettingsApi from 'api/v2/SettingsApi';
import UserApi from 'api/v2/UserApi';
import Vue from 'vue';

const { isMspAdminErrMsg, isMspStatusCode } = SettingsConsts;
const {
  administrator, administratorWithBilling, commandRunner, commandRunnerWithBilling, manager,
} = UserRoleConsts;
const growthModule = 'GrowthModule';
const onboardingModule = 'OnboardingModule';

export const defaultAdBridgeSyncAgentUrl = 'https://cdn02.jumpcloud.com/production/jumpcloud-adbridge-agent-2.3.2.msi';

const defaultUserSettings = SettingsConsts.userKeyValues.reduce((accumulator, keyValue) => {
  const { key, value } = keyValue;
  accumulator[key] = value;
  return accumulator;
}, {});

/**
 * @description Return the singleton object for use as a Vuex store module.
 */
export const createSettingsModule = () => {
  // Dynamically generate the initial state of the SettingsModule from the
  // keyValue constants.
  const getInitialState = () => {
    const settings = SettingsConsts.keyValues.reduce((accumulator, keyValue) => {
      const { key, value } = keyValue;
      accumulator[key] = value;
      return accumulator;
    }, {});

    settings.roles = [];
    settings.user = defaultUserSettings;
    settings.staticUrls = { ...KbArticleUrls };
    settings.disallowOrgCreation = false;

    return settings;
  };

  // Determine if a user's role has the desired scope
  const hasScope = (state, scopeName) => {
    const { roles } = state;
    const { roleName } = state.user;

    const roleForRoleName = roles.filter(role => role.name === roleName);

    return get(roleForRoleName, '[0].scopes', []).includes(scopeName);
  };

  return {
    namespaced: true,

    state: getInitialState(),

    getInitialState,
    hasScope,

    // --------------------------------------------------------------------------
    // Actions
    // --------------------------------------------------------------------------
    actions: {
      fetchSettings({ commit, dispatch }) {
        return SettingsApi.getGlobalSettings()
          .then((resp) => {
            if (resp) {
              commit('SET_GLOBAL_SETTINGS', resp);
              if (resp.SETTINGS && resp.SETTINGS.growthData) {
                const growthObjs = resp.SETTINGS.growthData;

                // Note: featureUsaage no longer part of growthModules
                // See: https://jumpcloud.atlassian.net/browse/PU-252
                if (resp.SETTINGS.growthData.featureUsage) {
                  dispatch('FeatureTrialModule/setOrgGrowthData', {
                    growthData: growthObjs,
                    pushChanges: false,
                  }, { root: true });

                  // Remove `featureUsage` so not added to growthModule below
                  delete growthObjs.featureUsage;
                }
                dispatch(`${growthModule}/setOrgGrowthData`, {
                  growthData: growthObjs,
                  pushChanges: false,
                }, { root: true });
              }
            }
          })
          .then(() => true)
          .catch((error) => {
            // Check to see if this is an MSP admin; if so resolve, otherwise throw the error
            if (error
                && error.response
                && error.response.status === isMspStatusCode
                && error.response.data.message === isMspAdminErrMsg) {
              return false;
            }

            throw error;
          });
      },

      fetchTopLevelSettings(context) {
        return RoleApi.fetch()
          .then((resp) => {
            if (resp) {
              context.commit('SET_TOP_LEVEL_SETTINGS', { roles: resp.results });
            }
          })
          .catch(() => {
            // Swallow the error; if this fails we've got bigger problems
          });
      },

      fetchUserSettings(context) {
        return UserApi.getSelf()
          .then((resp) => {
            if (resp) {
              context.commit('SET_USER_SETTINGS', resp);
              context.dispatch(`${onboardingModule}/setAdminGrowthData`, {
                growthData: resp.growthData || {},
                pushChanges: false,
              }, { root: true });
            }
          });
      },

      updateSettings(context, payload) {
        context.commit('UPDATE_GLOBAL_SETTINGS', payload);
        // Update the Organizations store so the org list view reflects the updated
        // information if the name or logo changed
        if (context.state.orgId) {
          // If the admin updated display information for an org, signal that store as well
          if (payload.name || payload.logoUrl) {
            const item = {
              _id: context.state.orgId,
              attributes: {
                displayName: payload.name,
                logoUrl: payload.logoUrl,
              },
            };
            context.dispatch('OrganizationsCollection/refreshCollection', { item }, { root: true });

            // Trigger an org event so that all open tabs can refresh the orgs list
            // and then remove so it doesn't pollute localStorage
            PageNavigationUtil.setLocalStorageItem(EventConsts.orgEvent, true);
            PageNavigationUtil.removeLocalStorageItem(EventConsts.orgEvent);
          }
        }
      },

      updateUserSettings(context, payload) {
        context.commit('UPDATE_USER_SETTINGS', payload);
      },
      async getProviderDetails({ commit, state }) {
        let response;
        const roleName = get(state, 'user.roleName', '');
        const providerId = get(state, 'user.provider');
        if (![commandRunner, commandRunnerWithBilling].includes(roleName)
              && !isUndefined(providerId)) {
          response = await ProvidersApi.getProviderDetails(
            providerId,
          );
        } else {
          response = { disallowOrgCreation: false };
        }
        const { disallowOrgCreation } = response;
        commit('SET_PROVIDER_ORG_CREATE', { disallowOrgCreation });
      },
    },

    // --------------------------------------------------------------------------
    // Getters
    // --------------------------------------------------------------------------
    getters: {
      getBetaFeatures(state = {}) {
        return state.betaFeatures || false;
      },

      getGoogleAppsKBUrl() {
        return KbArticleUrls.GoogleAppsKB;
      },

      getIsCustomer(state) {
        return state.isCustomer;
      },

      getLatestWhatsNewPublishedDate(state) {
        return state.latestWhatsNewPublishedDate;
      },

      getLdapKBUrl() {
        return KbArticleUrls.LdapKB;
      },

      getO365KBUrl() {
        return KbArticleUrls.O365KB;
      },

      getUser(state = { user: {} }) {
        return state.user;
      },

      getWorkdayKBUrl() {
        return KbArticleUrls.WorkdayKB;
      },

      getWorkdayKBCreateReportUrl() {
        return KbArticleUrls.WorkdayKBCreateReport;
      },

      hasBilling(state) {
        const roleName = get(state, 'user.roleName', '');
        return roleName && roleName.indexOf('With Billing') !== -1;
      },

      hasBillingScope(state) {
        return hasScope(state, UserScopeConsts.billing);
      },

      hasProvidersScope(state) {
        return hasScope(state, UserScopeConsts.providers);
      },

      hasRemoteAssistScope(state) {
        return hasScope(state, UserScopeConsts.remoteAssist);
      },

      hasRemoteAssistSessionsScope(state) {
        return hasScope(state, UserScopeConsts.remoteAssistSessions);
      },

      hasSupportScope(state) {
        return hasScope(state, UserScopeConsts.support);
      },

      hasSystemInsights(state) {
        return state.features.systemInsights.enabled;
      },

      hasWindowsMdmEnabled(state) {
        return get(state, 'windowsMDM.enabled', false);
      },

      hasWindowsMdmAutoEnrollEnabled(state) {
        return get(state, 'windowsMDM.autoEnroll', false);
      },

      isAdmin(state) {
        const roleName = get(state, 'user.roleName', '');
        return Boolean(roleName && roleName.indexOf(administrator) !== -1);
      },

      isAdminWithBilling(state) {
        const roleName = get(state, 'user.roleName', '');
        return Boolean(roleName && roleName.indexOf(administratorWithBilling) !== -1);
      },

      isCommandRunner(state) {
        const roleName = get(state, 'user.roleName', '');
        return roleName && roleName.indexOf(commandRunner) !== -1;
      },

      isManager(state) {
        const roleName = get(state, 'user.roleName', '');
        return roleName && roleName.indexOf(manager) !== -1;
      },

      /**
       * Determines if the current user is associated to a Provider
       * (i.e., they are a MTP Admin and not a tenant Admin).
       *
       * This method does not prove if the organization the user is associated
       * to belongs to a provider. See OrganizationApi or OrganizationCollection
       * for determining if the organization belongs to a provider.
       * @returns true if user is associated to a Provider, otherwise false.
       */
      isProvider(state) {
        return !!get(state, 'user.provider', '');
      },

      isProviderOrg(state, getters, rootState, rootGetters) {
        const orgDetails = rootGetters['OrganizationsCollection/getItemDetails'](state.orgId);
        return !!get(orgDetails, 'provider', false);
      },

      systemUsersCanEdit(state) {
        return state.systemUsersCanEdit;
      },
    },

    // --------------------------------------------------------------------------
    // Mutations
    // --------------------------------------------------------------------------
    mutations: {
      /**
       * @description Put all of the global settings values that we use into the state.  This method
       * flattens out the JSON object that we receive from the /api/settings endpoint in an
       * attempt to make these values a little bit easier to use.  For the most part, the names
       * remain the same (other than CAPS and '_' to camelCase).
       *
       * @todo window is being kept for legacy -
       * client/layout/views/HeaderPanel.js, and Command execution jade files
       *
       * @todo It would be useful to continue to separate these into org, system,
       * and site/session settings instead of having them in one big object.
       */
      SET_GLOBAL_SETTINGS(state = {}, payload = { LOCALS: {}, SETTINGS: {} }) {
        Vue.set(state, 'adBridgeSetupUrl', payload.LOCALS.adBridgeSetupUrl);

        // TEMP: Hard code URL if not available until HY-538 is resolved.
        // SEE: https://jumpcloud.atlassian.net/browse/HY-538
        Vue.set(state, 'adBridgeSyncAgentUrl', payload.LOCALS.adBridgeSyncAgentUrl || defaultAdBridgeSyncAgentUrl);

        Vue.set(state, 'betaFeatures', payload.SETTINGS.betaFeatures);
        Vue.set(state, 'clientSideRequestLimit', payload.CLIENT_SIDE_REQUEST_LIMIT);
        Vue.set(state, 'connectKey', payload.CONNECT_KEY);
        Vue.set(state, 'contactEmail', payload.SETTINGS.contactEmail || '');
        Vue.set(state, 'contactName', payload.SETTINGS.contactName || '');
        Vue.set(state, 'csrfToken', payload.CSRF_TOKEN);
        window.CSRF_TOKEN = payload.CSRF_TOKEN; // For legacy code
        Vue.set(state, 'directoriesConnectKey', payload.DIRECTORIES_CONFIG.connectKey);

        if (payload.GLOBAL_CONFIG) {
          Vue.set(state, 'directoryAgentVersion', payload.GLOBAL_CONFIG.identitySourceAgentVersion);
          Vue.set(state, 'syncAgentVersion', payload.GLOBAL_CONFIG.syncAgentVersion);
          Vue.set(state, 'latestWhatsNewPublishedDate', payload.GLOBAL_CONFIG.latestWhatsNewPublishedDate);
        }

        Vue.set(state, 'disableCommandRunner', payload.SETTINGS.disableCommandRunner);
        Vue.set(state, 'disableGoogleLogin', payload.SETTINGS.disableGoogleLogin);
        Vue.set(state, 'disableUM', payload.SETTINGS.disableUM);
        Vue.set(state, 'emailDisclaimer', payload.SETTINGS.emailDisclaimer || '');
        Vue.set(state, 'enableGoogleApps', payload.SETTINGS.enableGoogleApps);
        Vue.set(state, 'enableManagedUID', payload.SETTINGS.enableManagedUID);
        Vue.set(state, 'enableO365', payload.SETTINGS.enableO365);
        Vue.set(state, 'enableUserPortalAgentInstall', payload.SETTINGS.enableUserPortalAgentInstall);
        Vue.set(state, 'features', payload.SETTINGS.features);
        Vue.set(state, 'googleAnalyticsObject', 'ga');
        Vue.set(state, 'googleAppsUrl', payload.LOCALS.googleAppsUrl);
        Vue.set(state, 'hasContract', payload.HAS_CONTRACT);
        Vue.set(state, 'hasCreditCard', payload.HAS_CREDIT_CARD);
        Vue.set(state, 'idleSessionDurationMinutes', payload.SETTINGS.userPortal.idleSessionDurationMinutes);

        Vue.set(state, 'isCustomer', payload.IS_CUSTOMER);
        Vue.set(state, 'jit', payload.jit);
        Vue.set(state, 'kickstart', payload.LOCALS.kickstart);
        Vue.set(state, 'logoUrl', payload.logoUrl); // From /api/organizations
        Vue.set(state, 'macAgentUrl', payload.LOCALS.macAgentUrl);
        Vue.set(state, 'maxSystemUsers', payload.MAX_SYSTEM_USERS);
        Vue.set(state, 'name', payload.SETTINGS.name || '');
        Vue.set(state, 'newSystemUserStateDefaults', payload.SETTINGS.newSystemUserStateDefaults);
        Vue.set(state, 'orgId', payload.ORG_ID);
        Vue.set(state, 'passwordCompliance', payload.SETTINGS.passwordCompliance);
        Vue.set(state, 'passwordPolicy', payload.SETTINGS.passwordPolicy);
        Vue.set(state, 'role', payload.ROLE);
        Vue.set(state, 'stripeIdPresent', payload.STRIPE_ID_PRESENT);
        Vue.set(state, 'supportLevel', payload.SUPPORT_LEVEL);
        Vue.set(state, 'systemUserPasswordExpirationInDays', payload.SETTINGS.systemUserPasswordExpirationInDays);
        Vue.set(state, 'systemUsersCanEdit', payload.SETTINGS.systemUsersCanEdit);
        Vue.set(state, 'userNeedsPWReset', payload.USER_NEED_PW_RESET);
        window.USER_NEED_PW_RESET = payload.USER_NEED_PW_RESET; // For legacy code
        Vue.set(state, 'userPassSet', payload.USER_PASS_SET);
        window.USER_PASS_SET = payload.USER_PASS_SET; // For legacy code
        Vue.set(state, 'windowsAgentUrl', payload.LOCALS.windowsAgentUrl);
        Vue.set(state, 'windowsAgentMsiUrl', payload.LOCALS.windowsAgentMsiUrl);
        Vue.set(state, 'windowsMDM', payload.SETTINGS.windowsMDM);
      },

      /**
       * @description Put all of the top level configuration settings values that we use
       * into the state. For example - `roles`
       */
      SET_TOP_LEVEL_SETTINGS(state, payload) {
        Object.keys(payload).forEach((key) => {
          Vue.set(state, key, payload[key]);
        });
      },
      SET_PROVIDER_ORG_CREATE(state, payload) {
        Vue.set(state, 'disallowOrgCreation', payload.disallowOrgCreation);
      },
      /**
       * @description Put all of the user settings values that we use into the state.
       */
      SET_USER_SETTINGS(state, payload) {
        // ensure default values are used if payload does not include them
        Object.keys({ ...defaultUserSettings, ...payload }).forEach((key) => {
          if (key === '_id') {
            // Store payload._id in state.id
            Vue.set(state.user, 'id', payload._id);
          }
          Vue.set(state.user, key, payload[key]);
        });
      },

      // @todo If the USER password global fields in window change, need to pick those up?
      // These are still being used by legacy code
      //
      // @todo Figure out when we can remove this dependency
      //
      // window.CSRF_TOKEN = payload.CSRF_TOKEN; // For legacy code
      // window.USER_NEED_PW_RESET = payload.USER_NEED_PW_RESET;
      // window.USER_PASS_SET = payload.USER_PASS_SET;
      UPDATE_GLOBAL_SETTINGS(state, payload) {
        Object.keys(payload).forEach((key) => {
          Vue.set(state, key, payload[key]);
        });
      },

      UPDATE_USER_SETTINGS(state, payload) {
        Object.keys(payload).forEach((key) => {
          Vue.set(state.user, key, payload[key]);
        });
      },
    },
  };
};

export default createSettingsModule();
